import React, { useState, useEffect } from 'react';
import { IAttachmentOption } from 'pages/Conversations/interfaces/IAttachmentOption';
import { ChannelViewClinicPetFragment } from 'shared/types/graphql';
import PetAvatar from 'shared/components/Avatars/PetAvatar';
import {
  Checkbox,
  Text,
  VStack,
  HStack,
  Flex,
  ModalCloseButton,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooterButtons,
  useToast,
} from '@televet/kibble-ui';

interface ILinkPetToFormModalProps {
  isOpen: boolean;
  attachments: IAttachmentOption[];
  linkedPets: ChannelViewClinicPetFragment[];
  onAttachmentChange: (attachment: IAttachmentOption) => void;
  onClose: () => void;
}

// TODO: De-couple this modal from attachments so we can use the same one across the app
const LinkPetToFormModal = ({
  isOpen,
  attachments,
  linkedPets,
  onAttachmentChange,
  onClose,
}: ILinkPetToFormModalProps): JSX.Element => {
  const toast = useToast();
  const [attachmentOptions, setAttachmentOptions] = useState<IAttachmentOption[]>([]);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

  useEffect(() => {
    setAttachmentOptions([...attachments]);
  }, [attachments]);

  const toggleFunction = (attachmentDetail: IAttachmentOption, clinicPetId: string, index: number): void => {
    if (attachmentDetail.clinicPetIds?.includes(clinicPetId)) {
      const petId = attachmentDetail.clinicPetIds.filter((ids) => ids !== clinicPetId);
      attachmentDetail.clinicPetIds = petId;
    } else if (attachmentDetail?.clinicPetIds === undefined) {
      attachmentDetail.clinicPetIds = [clinicPetId];
    } else {
      attachmentDetail.clinicPetIds = [...attachmentDetail?.clinicPetIds, clinicPetId];
    }

    setAttachmentOptions((oldAttachmentOptions) => {
      const newAttachmentOptions = [...oldAttachmentOptions];
      newAttachmentOptions[index] = attachmentDetail;
      return newAttachmentOptions;
    });

    if (showErrorMessage) {
      isSubmissionValid();
    }
  };

  const toggleDoNotLinkPet = (index: number): void => {
    setAttachmentOptions((oldAttachmentOptions) => {
      const newAttachmentOptions = [...oldAttachmentOptions];
      newAttachmentOptions[index].doNotLinkPet = !newAttachmentOptions[index].doNotLinkPet;
      newAttachmentOptions[index].clinicPetIds = [];
      return newAttachmentOptions;
    });

    if (showErrorMessage) {
      isSubmissionValid();
    }
  };

  const isSelectedFunction = (index: number, id?: string): boolean => {
    if (id) {
      return attachmentOptions[index]?.clinicPetIds?.includes(id) || false;
    }

    return attachmentOptions[index]?.doNotLinkPet || false;
  };

  const isSubmissionValid = (): boolean => {
    const hasInvalidForms = attachmentOptions.some(
      (attachment) => !attachment.clinicPetIds?.length && !attachment.doNotLinkPet,
    );

    setShowErrorMessage(hasInvalidForms);

    return !hasInvalidForms;
  };

  const handleChangeInAttachment = (): void => {
    if (!isSubmissionValid()) {
      toast({
        title: 'Error',
        description: 'We were unable to save your changes.',
        status: 'error',
      });
      return;
    }

    attachmentOptions.map((attachment) => {
      onAttachmentChange({ ...attachment });
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} size="sm" isCentered onClose={onClose}>
      <ModalCloseButton />
      <ModalHeader>Link Individuals to Forms</ModalHeader>
      <ModalBody>
        <VStack align="flex-start">
          <Text>{`Select the ${
            linkedPets.length > 1 ? 'pet(s)' : 'pet'
          } you want to be associated with each form.`}</Text>
          {showErrorMessage && (
            <Alert
              status="error"
              title="Your selection is invalid"
              description="One or more forms is missing a required selection."
              hideCloseButton
            />
          )}
          <VStack align="flex-start">
            {attachments.map((attachment, index) => {
              return (
                <VStack key={`${attachment.id}-options-${index}`} align="flex-start" spacing={0}>
                  <Text fontWeight="bold" mb={2}>
                    {attachment.text}
                  </Text>
                  {linkedPets.map((pet, ind) => {
                    return (
                      <Flex key={`${attachment.id}-linkedPet-${ind}`} pb="12px">
                        <Checkbox
                          isDisabled={attachment.doNotLinkPet}
                          isChecked={isSelectedFunction(index, pet.id)}
                          onChange={(): void => toggleFunction(attachment, pet.id, index)}
                        >
                          <HStack>
                            <PetAvatar
                              size="sm"
                              speciesType={pet?.speciesType}
                              isDeceased={pet?.computedIsDeceased}
                              iconOnly
                            />
                            <Text size="sm">{pet?.name || ''}</Text>
                          </HStack>
                        </Checkbox>
                      </Flex>
                    );
                  })}
                  <Checkbox
                    defaultChecked
                    isChecked={isSelectedFunction(index)}
                    onChange={(): void => toggleDoNotLinkPet(index)}
                  >
                    <Text size="sm">Do not link a pet</Text>
                  </Checkbox>
                </VStack>
              );
            })}
          </VStack>
        </VStack>
      </ModalBody>
      <ModalFooterButtons submitText="Confirm" onSubmit={handleChangeInAttachment} />
    </Modal>
  );
};

export default React.memo(LinkPetToFormModal);
